import type { ReactNode } from "react";
import { useIntl } from "react-intl";

import type { Sentence } from "@/features/collaboration/lib/sentence";
import { cn } from "@/shared/lib/cn";

const STRONG = "font-medium text-ink";

function renderTarget(sentence: Sentence): ReactNode {
  const target = String(sentence.values.target ?? "");
  if (sentence.targetKind === "phrase") return target;
  if (sentence.targetKind === "reference") {
    return (
      <bdi key="target" dir="ltr" className={cn(STRONG, "whitespace-nowrap")}>
        {target}
      </bdi>
    );
  }
  return (
    <bdi key="target" className={cn(STRONG, "user-text")}>
      {target}
    </bdi>
  );
}

/**
 * A feed or notification sentence with the people and the thing it is about emphasised. Names
 * are isolated (<bdi>) so an Arabic name inside an English sentence, or the other way round,
 * keeps its own reading order; task references always read left to right.
 */
export function RichSentence({ sentence, className }: { sentence: Sentence; className?: string }) {
  const intl = useIntl();
  const rich: Record<string, ReactNode> = { ...sentence.values, target: renderTarget(sentence) };
  // People's names and words keep their own direction; labels from the catalog do not need to.
  for (const key of ["actor", "name", "title"]) {
    const value = sentence.values[key];
    if (value !== undefined) {
      rich[key] = (
        <bdi key={key} className={STRONG}>
          {value}
        </bdi>
      );
    }
  }
  for (const key of ["status", "priority"]) {
    const value = sentence.values[key];
    if (value !== undefined) {
      rich[key] = (
        <span key={key} className={STRONG}>
          {value}
        </span>
      );
    }
  }
  return <span className={className}>{intl.formatMessage({ id: sentence.id }, rich)}</span>;
}
